import { Plane, Car, Hotel } from "lucide-react";
import hoodRiverShuttleMap from "@/assets/hood-river-shuttle-map.png";
import footerBg from "@/assets/footer-bg.jpg";

export function Travel() {
  return (
    <div className="min-h-screen bg-background">
      <article className="pt-24 md:pt-32 pb-20 mx-auto px-6 max-w-175">
        <h1 className="font-display text-4xl text-foreground text-center mb-12">
          Travel
        </h1>

        <div className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <Plane className="w-6 h-6 text-muted-foreground" strokeWidth={1.5} />
            <h2 className="font-display text-xl md:text-2xl text-foreground">Getting In</h2>
          </div>
          <div className="pl-9">
            <p className="font-body text-muted-foreground leading-relaxed">
              The closest airport is Portland International Airport (PDX), which is about a one hour drive to Hood River.
            </p>
          </div>
        </div>

        <div className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <Car className="w-6 h-6 text-muted-foreground" strokeWidth={1.5} />
            <h2 className="font-display text-xl md:text-2xl text-foreground">Getting Around</h2>
          </div>
          <div className="pl-9">
            <p className="font-body text-muted-foreground leading-relaxed">
              Uber and Lyft are NOT available in Hood River. We recommend renting a car at the airport, or coordinating with other guests to carpool. We will also be providing shuttle service to and from the venue on the wedding day.
            </p>
          </div>
        </div>

        <div className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <Hotel className="w-6 h-6 text-muted-foreground" strokeWidth={1.5} />
            <h2 className="font-display text-xl md:text-2xl text-foreground">Where to Stay</h2>
          </div>
          <div className="pl-9">
            <div className="mb-8">
              <h3 className="font-display text-lg text-foreground mb-3">Option 1: Stay in Hood River (Recommended if booking early)</h3>

              <div className="mb-4">
                <p className="font-body text-foreground font-medium mb-2">How to book</p>
                <p className="font-body text-muted-foreground leading-relaxed">
                  If you plan on taking the shuttle, book within easy walking distance from the two shuttle stops:
                </p>
                <ul className="font-body text-muted-foreground mt-2 space-y-1">
                  <li>• <strong>Stop 1:</strong> <a href="https://maps.app.goo.gl/oRYegocN5ji3JVJK7" target="_blank" rel="noopener noreferrer" className="text-primary underline hover:text-primary/80 transition-colors">Holiday Inn Express, Hood River</a></li>
                  <li>• <strong>Stop 2:</strong> <a href="https://maps.app.goo.gl/Aw2J8SsPEvQCd4eg9" target="_blank" rel="noopener noreferrer" className="text-primary underline hover:text-primary/80 transition-colors">Hood River Hotel</a> (in Downtown Hood River)</li>
                </ul>
                <img src={hoodRiverShuttleMap} alt="Map showing shuttle stops at Holiday Inn and Downtown Hood River" className="mt-4 rounded-lg w-full" />
              </div>

              <div className="mb-4">
                <p className="font-body text-foreground font-medium mb-2">Pros</p>
                <ul className="font-body text-muted-foreground space-y-1">
                  <li>• Very convenient access to venue (&lt;15min drive)</li>
                  <li>• Easy to attend welcome drinks in Hood River the night before if interested</li>
                  <li>• Ability to explore more of the Oregon Gorge area (the drive to Hood River has a ton of waterfalls, hikes, and scenic viewpoints!)</li>
                </ul>
              </div>

              <div className="mb-4">
                <p className="font-body text-foreground font-medium mb-2">Cons</p>
                <ul className="font-body text-muted-foreground space-y-1">
                  <li>• Accommodations are very limited due to peak wedding season – they get expensive and book out quickly so please book ASAP if you prefer this option!</li>
                  <li>• Requires a car rental from Portland</li>
                </ul>
              </div>

              <p className="font-body text-muted-foreground leading-relaxed italic">
                🏠 If you're able to get a group together, Airbnbs can be a great option in the area. Just make sure it's walking distance from the shuttle stops if you plan on taking the shuttle!
              </p>
              <p className="font-body text-muted-foreground leading-relaxed italic mt-4">
                ⚠️ We recommend not staying in Washington (north of Columbia River). The bridge connecting Oregon and Washington undergoes periodic closures that are hard to plan around.
              </p>
            </div>

            <div>
              <h3 className="font-display text-lg text-foreground mb-3">Option 2: Stay in Downtown Portland</h3>

              <div className="mb-4">
                <p className="font-body text-foreground font-medium mb-2">How to book</p>
                <p className="font-body text-muted-foreground leading-relaxed">
                  If you plan on staying in Portland, please book using our <a href="https://www.hyatt.com/shop/rooms/pdxct?location=Hyatt%20Centric%20Downtown%20Portland&checkinDate=2026-08-27&checkoutDate=2026-08-29&rooms=1&adults=1&kids=0&corp_id=g-ajwb" target="_blank" rel="noopener noreferrer" className="text-primary underline hover:text-primary/80 transition-colors">room block link for the Hyatt Centric Downtown Portland</a> (27th-30th available in block). The shuttle will be departing at 1:40pm from the hotel.
                </p>
              </div>

              <div className="mb-4">
                <p className="font-body text-foreground font-medium mb-2">Pros</p>
                <ul className="font-body text-muted-foreground space-y-1">
                  <li>• Generally more affordable with more lodging options</li>
                  <li>• Lower pressure to book early if plans not solidified</li>
                  <li>• No car rental needed if taking shuttle</li>
                </ul>
              </div>

              <div>
                <p className="font-body text-foreground font-medium mb-2">Cons</p>
                <ul className="font-body text-muted-foreground space-y-1">
                  <li>• Travel times are quite long on wedding day due to distance and multiple shuttle stops</li>
                  <li>• Expect ~1h 45min of travel time going to venue with a departure time of 1:40pm from hotel</li>
                  <li>• Expect ~1h 30min of travel time returning from the venue with a departure time of ~9:45pm</li>
                </ul>
              </div>
            </div>

            <p className="font-body text-muted-foreground leading-relaxed mt-8 font-semibold">
              📣 If you're looking for others to book with or have any questions, please reach out to Jamie or Andrew!
            </p>
          </div>
        </div>
      </article>

      <img src={footerBg} alt="" className="w-full" />
    </div>
  );
}
